import { css } from '@emotion/css';

import { mobileMediaQuery, nonMobileMediaQuery } from '../../constants';
import { h2Css, m, MotifScheme, p1Css } from '../../motif';
import { globalHeaderHeight } from '../GlobalHeader';

export const immersiveScrollBlockCss = css`
  background-color: ${m('--bg-color')};
`;

export const boundaryCss = css`
  padding: ${m('--spacing-xxxl')} ${m('--spacing-l')};

  ${nonMobileMediaQuery} {
    padding-inline: ${m('--spacing-xxxxl')};
  }
`;

export const contentWrapperCss = css`
  display: grid;
  gap: ${m('--spacing-xl')};
  grid-template-columns: 1fr;

  ${nonMobileMediaQuery} {
    grid-template-areas: 'text text text text text . cards cards cards cards cards cards';
    grid-template-columns: repeat(12, 1fr);
  }
`;

export const contentWrapperStartAlignmentCss = css`
  ${nonMobileMediaQuery} {
    grid-template-areas: 'cards cards cards cards cards cards . text text text text text';
  }
`;

export const textColumnCss = css`
  grid-column: span 1;

  ${nonMobileMediaQuery} {
    align-items: center;
    display: flex;
    grid-area: text;
    height: calc(100vh - ${globalHeaderHeight}px);
    position: sticky;
    top: ${globalHeaderHeight}px;
  }
`;

export const textColumnStartAlignmentCss = css`
  ${nonMobileMediaQuery} {
    flex-direction: row-reverse;
  }
`;

export const navigationWrapperCss = css`
  display: none;
  list-style: none;

  > li {
    line-height: 0;
  }

  ${nonMobileMediaQuery} {
    display: flex;
    flex-direction: column;
    flex: 0 0 20%;
    gap: ${m('--spacing-m')};
  }
`;

export const navigationWrapperAlignmentCss = css`
  ${nonMobileMediaQuery} {
    align-items: flex-end;
  }
`;

const activeColor = m('--neutral-v700');
const activeDarkColor = m('--primary-v100');

export const navigationElementCss = (motifScheme: MotifScheme) => css`
  background-color: transparent;
  height: 24px;
  transition: height 0.3s ease-in-out, background-color 0.3s ease-in-out;
  width: 14px;

  &::before {
    background-color: rgba(133, 141, 148, 0.5);
    border-radius: ${m('--border-radius-s')};
    content: '';
    display: block;
    height: 100%;
    width: 4px;
  }

  @media (hover: hover) {
    cursor: pointer;

    &:hover {
      height: 56px;
    }

    &:hover::before {
      background-color: ${motifScheme === MotifScheme.SECONDARY ? activeDarkColor : activeColor};
    }
  }
`;

export const navigationElementActiveCss = (motifScheme: MotifScheme) => css`
  height: 56px;

  &::before {
    background-color: ${motifScheme === MotifScheme.SECONDARY ? activeDarkColor : activeColor};
  }
`;

export const textWrapperCss = css`
  color: ${m('--fg-color')};

  ${mobileMediaQuery} {
    display: flex;
    flex-direction: column;
    flex: 0 0 80%;
    gap: ${m('--spacing-m')};
    padding: ${m('--spacing-m')};
  }

  ${nonMobileMediaQuery} {
    align-self: center;
    flex: 1;
    padding-block: 134px;
  }
`;

export const textStartCss = css`
  ${mobileMediaQuery} {
    text-align: start;
  }
`;

export const textCenterCss = css`
  ${mobileMediaQuery} {
    text-align: center;
  }
`;

export const textEndCss = css`
  ${mobileMediaQuery} {
    text-align: end;
  }
`;

export const cardsColumnCss = css`
  grid-column: span 1;
  list-style: none;

  ${mobileMediaQuery} {
    display: flex;
    flex-direction: column;
    gap: ${m('--spacing-m')};
  }

  ${nonMobileMediaQuery} {
    grid-area: cards;
  }
`;

export const cardWrapperCss = css`
  align-items: center;
  display: flex;
  justify-content: center;

  > div {
    cursor: initial;
  }

  ${mobileMediaQuery} {
    transform: scale(0.7);
    transition: transform 50ms linear;
    will-change: transform;
  }

  ${nonMobileMediaQuery} {
    padding-block: 134px;
    scroll-margin-top: ${globalHeaderHeight}px;

    /* Override the Carousel Card width. Value defined by the design team. */
    > div {
      width: 320px;
    }
  }
`;

export const eyebrowCss = css`
  /**
   * using var instead of m function because this is not a motif variable.
   * It is a custom css var defined in heroCss.
   */
  color: ${m('--fg-color')};
  font-size: ${m('--annotation-desktop-font-size')};
  font-weight: ${m('--annotation-desktop-font-weight')};
  letter-spacing: ${m('--annotation-desktop-font-letter-spacing')};
  line-height: ${m('--annotation-desktop-font-line-height')};

  ${mobileMediaQuery} {
    font-size: ${m('--annotation-mobile-font-size')};
    font-weight: ${m('--annotation-mobile-font-weight')};
    letter-spacing: ${m('--annotation-mobile-font-letter-spacing')};
    line-height: ${m('--annotation-mobile-font-line-height')};
  }
`;

export const titleCss = css`
  ${h2Css}
  margin-block-start: ${m('--spacing-xs')};
`;

export const textCss = css`
  ${p1Css}

  ${nonMobileMediaQuery} {
    margin-block-start: ${m('--spacing-m')};
  }
`;
